/**
 * The org lists (distributors / MSPs / companies), fetched once and shared.
 *
 * `useScopeHierarchy` used to hold this state per call site and fetch in its
 * own effect. It is called 29 times across 19 files -- the layout, the sidebar,
 * the section rail, the org target picker and every page -- so one hard refresh
 * issued up to a dozen identical `page_size=500` requests, and each instance
 * flipped its own `isLoading` at its own moment. That is why the scope bar, the
 * sidebar, the rail and the page body all settled at different times and the
 * screen appeared to keep changing after it had loaded.
 *
 * One module-level cache, one in-flight promise, one set of subscribers. The
 * shape is copied from `useRoleCatalog`, which already solved this for the role
 * catalog; the addition here is that the data varies by role, so the cache is
 * keyed on what the request actually depends on.
 *
 * Consumed through `useSyncExternalStore`, which is the supported way to read
 * module state from React: it subscribes before paint and guarantees every
 * caller in a render sees the same snapshot, so two components can no longer
 * disagree about whether the lists have arrived.
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { companiesApi, type Company } from "@/lib/api/companies";
import { distributorsApi, type Distributor } from "@/lib/api/distributors";
import { mspsApi, type MSP } from "@/lib/api/msps";
import { onOrganizationsChanged } from "@/lib/api/orgEvents";
import { markScopeCorrection, tokenOf } from "@/lib/scope/orgParam";
import { useContextStore } from "@/store/contextStore";

export interface ScopeData {
  distributors: Distributor[];
  msps: MSP[];
  companies: Company[];
  /**
   * The MSP list is the complete set, not a truncated page.
   *
   * An explicit signal rather than a length proxy: the visible-MSP gate on
   * companies must not hide a company whose MSP simply was not fetched.
   */
  mspsComplete: boolean;
  /**
   * One of the three lists failed to load, so an organization missing from
   * the lists may still be real. `error` cannot say this on its own: it also
   * carries the "previous selection no longer exists" message.
   */
  incomplete: boolean;
  isLoading: boolean;
  error: string | null;
}

/** What the request depends on. A change here means a different result set. */
export interface ScopeQuery {
  /**
   * Who is asking.
   *
   * Part of the key because the backend scopes these lists to the caller:
   * impersonating a different user with the *same* role would otherwise hit the
   * cache and show the previous user's organizations. Role alone is not
   * identity.
   */
  userId: string | null;
  /**
   * Platform staff, the only callers the server lets view the whole platform
   * (`X-Context-Scope: all`). Decides what a dead selection is reset to.
   */
  isPlatformStaff: boolean;
  canSeeDistributors: boolean;
  needsMspList: boolean;
  isCompanyLevel: boolean;
}

const EMPTY: ScopeData = {
  distributors: [],
  msps: [],
  companies: [],
  mspsComplete: false,
  incomplete: false,
  isLoading: true,
  error: null,
};

let snapshot: ScopeData = EMPTY;
let cacheKey: string | null = null;
const subscribers = new Set<() => void>();

function keyOf(q: ScopeQuery): string {
  return [
    q.userId ?? "anon",
    q.isPlatformStaff ? "p" : "-",
    q.canSeeDistributors ? "d" : "-",
    q.needsMspList ? "m" : "-",
    q.isCompanyLevel ? "c" : "-",
  ].join("|");
}

/** Replace the snapshot and wake every subscriber. */
function publish(next: ScopeData): void {
  snapshot = next;
  subscribers.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function getSnapshot(): ScopeData {
  return snapshot;
}

/**
 * Drop a selection that no longer exists.
 *
 * `contextStore` persists the scope *and* the chosen id, so a company that has
 * since been deleted -- or a database that was rebuilt -- leaves a dead id
 * behind that the interceptor keeps sending as `X-Context-Id`. The failure then
 * surfaces somewhere unrelated, e.g. adding a credential returning 400 "Select
 * a specific company scope" while the scope bar still displays that company.
 *
 * Only clears when the list it should belong to actually loaded: a rejected
 * request means "unknown", not "gone", and treating them alike would reset a
 * working selection on a transient blip.
 *
 * Platform staff are reset to the whole platform. Everybody else is reset to
 * their own default scope: the server refuses "all" below platform staff, so
 * resetting a distributor there turned every later request into a 403.
 *
 * Living here rather than in the hook is a fix in itself -- it used to run once
 * per hook instance, so a dozen copies raced to reset the same context.
 */
function dropMissingSelection(
  q: ScopeQuery,
  dists: PromiseSettledResult<Distributor[]>,
  tens: PromiseSettledResult<MSP[]>,
  comps: PromiseSettledResult<Company[]>,
): string | null {
  const ctx = useContextStore.getState();
  const missing =
    (ctx.scope === "company" &&
      ctx.companyId &&
      comps.status === "fulfilled" &&
      !comps.value.some((c) => String(c.id) === String(ctx.companyId))) ||
    (ctx.scope === "msp" &&
      ctx.mspId &&
      tens.status === "fulfilled" &&
      !tens.value.some((m) => String(m.id) === String(ctx.mspId))) ||
    (ctx.scope === "distributor" &&
      ctx.distributorId &&
      dists.status === "fulfilled" &&
      !dists.value.some((d) => String(d.id) === String(ctx.distributorId)));

  if (!missing) return null;
  const name = ctx.getDisplayName();
  if (q.isPlatformStaff) ctx.setAllContext();
  else ctx.clearContext();
  // A correction, so the address (`?org=`) replaces its entry rather than
  // pushing one, and explains the reset unless it shows another organization
  // from the address instead.
  markScopeCorrection(tokenOf(useContextStore.getState()), "unknown");
  return (
    `The previously selected scope${name ? ` (${name})` : ""} no longer ` +
    "exists, so the view was reset. Pick a scope to continue."
  );
}

function load(q: ScopeQuery): Promise<void> {
  // A company-level user has no lists to choose between; the backend already
  // scopes them to their own company.
  if (q.isCompanyLevel) {
    publish({ ...EMPTY, isLoading: false });
    return Promise.resolve();
  }

  publish({ ...snapshot, isLoading: true, mspsComplete: false, error: null });

  // `allSettled`, not `all`: three independent lists, and with `all` a single
  // rejection discarded the two that succeeded, leaving every scope dropdown
  // empty because one unrelated endpoint failed.
  //
  // `branch`, not the default. With no header the request interceptor fills one
  // in from the scope bar, so these lists shrank to whatever the bar held: with
  // an MSP selected the distributor list came back empty, and every org picker
  // built on it (resale rates, Desk and AI settings) said "No distributors in
  // your scope" to a platform admin. These are the navigator's lists -- what
  // the caller may choose between -- so they are always the caller's own branch.
  return (
    Promise.allSettled([
      q.canSeeDistributors
        ? distributorsApi.list({ scope: "branch" })
        : Promise.resolve<Distributor[]>([]),
      q.needsMspList
        ? mspsApi.list({ scope: "branch" })
        : Promise.resolve<MSP[]>([]),
      companiesApi.list({ scope: "branch" }),
    ])
      .then(([dists, tens, comps]) => {
        const failed = [dists, tens, comps].some(
          (r) => r.status === "rejected",
        );
        // Never allowed to sink the publish below: this touches `contextStore`,
        // and if it throws every subscriber is left loading forever. The previous
        // per-hook version survived that by clearing its flag in a `.finally`.
        let resetMessage: string | null = null;
        try {
          resetMessage = dropMissingSelection(q, dists, tens, comps);
        } catch {
          resetMessage = null;
        }
        publish({
          distributors: dists.status === "fulfilled" ? dists.value : [],
          msps: tens.status === "fulfilled" ? tens.value : [],
          companies: comps.status === "fulfilled" ? comps.value : [],
          // Complete when the fetch succeeded, because `list()` now follows
          // the server's pages to the end. The heuristic this replaces --
          // "a full page may hide more rows" -- compared the result against
          // 500 while the server capped a page at 100, so it read `true` for
          // every truncated list it was meant to catch.
          mspsComplete: tens.status === "fulfilled",
          incomplete: failed,
          isLoading: false,
          error:
            resetMessage ??
            (failed ? "Some scope data could not be loaded." : null),
        });
      })
      // A backstop, not an expectation. `allSettled` cannot reject, so reaching
      // here means the publish path itself failed — and a stuck `isLoading` is
      // the one outcome no consumer can recover from.
      .catch(() => {
        publish({
          ...snapshot,
          incomplete: true,
          isLoading: false,
          error: "Scope data could not be loaded.",
        });
      })
  );
}

/** Fetch unless the same query is already cached or in flight. */
function ensure(q: ScopeQuery): void {
  const key = keyOf(q);
  // Already loaded, or already loading this exact query: subscribers will be
  // published to when it lands. This is the dedup that turns a dozen concurrent
  // identical requests into one.
  // `cacheKey` is set *before* the request starts, so every other consumer
  // mounting in the same commit returns here rather than firing its own copy.
  // That single line is what collapses a dozen identical requests into one.
  if (key === cacheKey) return;
  cacheKey = key;
  void load(q);
}

/** Discard the cache and refetch. Shared, so one call refreshes every reader. */
export function refreshScopeData(): void {
  if (!lastQuery) return;
  cacheKey = null;
  ensure(lastQuery);
}

let lastQuery: ScopeQuery | null = null;

// A created, renamed, moved or deleted organization changes these lists.
onOrganizationsChanged(refreshScopeData);

/**
 * Subscribe to the shared org lists.
 *
 * Every caller in a render tree sees one snapshot and one `isLoading`, so the
 * shell and the page can no longer settle at different moments.
 */
export function useScopeData(
  q: ScopeQuery,
): ScopeData & { refresh: () => void } {
  lastQuery = q;
  const data = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // In an effect, not during render: `ensure` publishes, and publishing during
  // another component's render is what React warns about.
  useEffect(() => {
    ensure(q);
    // Spread rather than the object: `q` is rebuilt every render, so depending
    // on its identity would re-run this on every render of every consumer.
    // `q` is rebuilt every render by design; the fields below are what the
    // request actually depends on, and depending on the object identity would
    // re-run this on every render of every consumer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    q.userId,
    q.isPlatformStaff,
    q.canSeeDistributors,
    q.needsMspList,
    q.isCompanyLevel,
  ]);

  const refresh = useCallback(() => refreshScopeData(), []);
  return { ...data, refresh };
}

/**
 * The shared lists as they stand, without asking for them.
 *
 * For a reader that must not decide what is fetched: the address-held
 * selection reads these lists to learn which level an organization id sits at,
 * and the request belongs to `useScopeHierarchy`, which knows the caller's
 * role and is mounted beside it in the layout.
 */
export function useScopeDataSnapshot(): ScopeData {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test seam: forget everything between cases. */
export function __resetScopeDataForTests(): void {
  snapshot = EMPTY;
  cacheKey = null;
  lastQuery = null;
  subscribers.clear();
}
